// © 2025 Camp Promethean, LLC. All Rights Reserved.
// Types for the enhanced selection data model

import { SensoryData } from './sensory';

/**
 * Selection interface representing a selection in the database
 */
export interface Selection {
  id: string;
  pheno_id: string;
  plant_id: string;
  generation: string;
  selection_date: string;
  selection_rationale?: string;
  selection_notes?: string;
  selection_highlights?: string[];
  growth_metrics: {
    structure: number;
    vigor: number;
    yield: number;
    frost: number;
    overall: number;
  };
  project_id: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
}

/**
 * SelectionTag interface for tag data
 */
export interface SelectionTag {
  id: string;
  name: string;
  category: string;
}

/**
 * SelectionBadge interface for badge data
 */
export interface SelectionBadge {
  id: string;
  name: string;
  description: string;
  color: string;
}

/**
 * EnhancedSelection interface with additional UI-related properties
 */
export interface EnhancedSelection {
  id: string;
  phenoId: string;
  plantId: string;
  generation: string;
  selectionDate: string;
  selectionRationale?: string;
  selectionNotes?: string;
  selectionHighlights?: string[];
  growthMetrics: {
    structure: number;
    vigor: number;
    yield: number;
    frost: number;
    overall: number;
  };
  sensoryData?: SensoryData;
  tags: SelectionTag[];
  badges: SelectionBadge[];
  images?: string[];
}

/**
 * Convert database Selection to EnhancedSelection format
 */
export function mapSelectionToEnhanced(
  selection: Selection, 
  sensoryData?: SensoryData | null,
  tags: SelectionTag[] = [],
  badges: SelectionBadge[] = []
): EnhancedSelection {
  return {
    id: selection.id,
    phenoId: selection.pheno_id,
    plantId: selection.plant_id,
    generation: selection.generation,
    selectionDate: selection.selection_date,
    selectionRationale: selection.selection_rationale,
    selectionNotes: selection.selection_notes,
    selectionHighlights: selection.selection_highlights,
    growthMetrics: selection.growth_metrics,
    sensoryData: sensoryData || undefined,
    tags,
    badges,
    images: [] // Images would be populated separately
  };
}

/**
 * Convert EnhancedSelection to database Selection format
 */
export function mapEnhancedToSelection(
  enhanced: EnhancedSelection,
  projectId: string
): Omit<Selection, 'id' | 'created_at' | 'updated_at'> {
  return {
    pheno_id: enhanced.phenoId,
    plant_id: enhanced.plantId,
    generation: enhanced.generation,
    selection_date: enhanced.selectionDate,
    selection_rationale: enhanced.selectionRationale,
    selection_notes: enhanced.selectionNotes,
    selection_highlights: enhanced.selectionHighlights,
    growth_metrics: enhanced.growthMetrics,
    project_id: projectId
  };
}

// Predefined badge types
export const BADGE_TYPES = {
  TOP_PRODUCER: 'top-producer',
  FROST_MONSTER: 'frost-monster',
  AROMA_STAR: 'aroma-star',
  STRUCTURE_CHAMPION: 'structure-champion',
  SMOOTH_SMOKE: 'smooth-smoke',
  FLAVOR_PROFILE: 'flavor-profile',
  BREEDING_POTENTIAL: 'breeding-potential'
};

// Form data for creating/editing a selection
export interface SelectionFormData {
  phenotypeId: string;
  plantId: string;
  generation: string;
  reason: string;
  traitsSelected: string[];
  selectionNotes?: string;
  selectionRationale?: string;
  selectionHighlights?: string[];
  tags: string[];
}

/**
 * Create new enhanced selection from seed data
 */
export function createNewEnhancedSelection(plantData: any, projectId: string): EnhancedSelection {
  return {
    id: '', // Will be assigned by the database
    phenoId: plantData.phenotype_id,
    plantId: plantData.plant_id,
    generation: plantData.generation || 'F1',
    selectionDate: new Date().toISOString(),
    selectionRationale: '',
    selectionNotes: '',
    selectionHighlights: [],
    growthMetrics: {
      structure: 5,
      vigor: 5,
      yield: 5,
      frost: 5,
      overall: 5
    },
    sensoryData: undefined,
    tags: [],
    badges: [],
    images: []
  };
} 